<script setup>
/* global axios */
import { computed, ref, watch, onUnmounted, unref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import WebCallButton from './WebCallButton.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const webCallButtonRef = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);
const activeChat = computed(() => {
  if (currentChat.value?.id) return currentChat.value;
  if (props.chat?.id) return props.chat;
  return {};
});
const activeChatId = computed(() => activeChat.value?.id);

const hasCurrentChatId = computed(() => {
  const chat = activeChat.value;
  return chat && chat.id;
});
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => activeChat.value?.meta || props.chat?.meta || {});

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() => {
  const senderId =
    activeChat.value?.meta?.sender?.id ||
    props.chat?.meta?.sender?.id ||
    currentChat.value?.meta?.sender?.id;
  if (!senderId) return { name: '', phone_number: '', thumbnail: '', availability_status: '' };
  return store.getters['contacts/getContact'](senderId) || { name: '', phone_number: '', thumbnail: '', availability_status: '' };
});

const isSnoozed = computed(
  () => activeChat.value?.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = activeChat.value || {};
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const currentUser = computed(() => store.getters.getCurrentUser);

const toBool = value => {
  if (value === true) return true;
  if (value === false) return false;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true') return true;
    if (v === 'false') return false;
  }
  return false;
};

const conversationCustomAttributes = computed(() => {
  // NOTE: Some API payloads keep custom_attributes at root, some under meta.custom_attributes.
  // Merge to avoid flapping when one side is an empty object.
  return {
    ...(activeChat.value?.meta?.custom_attributes || {}),
    ...(activeChat.value?.custom_attributes || {}),
  };
});

const webcallRequestStatus = computed(() => {
  return conversationCustomAttributes.value?.webcall_request_status || '';
});

const optimisticRequest = ref(null); // { status: 'pending', mode: 'pbx'|'webrtc', at: ms }

watch(
  () => [activeChatId.value, webcallRequestStatus.value],
  ([, status]) => {
    // Clear optimistic once backend state arrives OR request is cleared (e.g. timeout cleanup sets status -> '').
    if (!optimisticRequest.value) return;
    if (status !== 'pending') optimisticRequest.value = null;
  }
);

const webcallRequestBannerText = computed(() => {
  if (!webcallRequestStatus.value && optimisticRequest.value?.status === 'pending') {
    return t('CONVERSATION.HEADER.WEBCALL.REQUEST_SENT');
  }
  if (webcallRequestStatus.value === 'pending') {
    return t('CONVERSATION.HEADER.WEBCALL.REQUEST_SENT');
  }
  if (webcallRequestStatus.value === 'rejected') {
    return t('CONVERSATION.HEADER.WEBCALL.REQUEST_REJECTED');
  }
  if (webcallRequestStatus.value === 'timeout') {
    return t('CONVERSATION.HEADER.WEBCALL.REQUEST_TIMEOUT');
  }
  if (webcallRequestStatus.value === 'accepted') {
    return t('CONVERSATION.HEADER.WEBCALL.REQUEST_ACCEPTED');
  }
  return '';
});

const webcallRequestDisabledReason = computed(() => {
  const config = window.chatwootConfig || {};
  if (!activeChatId.value) return t('CONVERSATION.HEADER.WEBCALL.CONVERSATION_NOT_FOUND');
  if (webcallDisabledByAgent.value) return t('CONVERSATION.HEADER.WEBCALL.DISABLED_BY_AGENT');
  if (!config.webcallBaseUrl) return t('CONVERSATION.HEADER.WEBCALL.ENV_NOT_CONFIGURED');
  if (!currentUser.value?.n_santral_id || !currentUser.value?.c_santral_dahili) {
    return t('CONVERSATION.HEADER.WEBCALL.AGENT_SANTRAL_MISSING');
  }
  return '';
});

// Also check conversation custom_attributes for webcall_active (for widget-initiated calls)
const webcallActiveFromConversation = computed(() => {
  return toBool(conversationCustomAttributes.value?.webcall_active);
});

const webcallMode = computed(() => {
  const mode = conversationCustomAttributes.value?.webcall_mode;
  if (mode === 'pbx' || mode === 'webrtc') return mode;
  return '';
});

const webcallModeLabel = computed(() => {
  if (webcallMode.value === 'pbx') return t('CONVERSATION.HEADER.WEBCALL.MODE_PBX');
  if (webcallMode.value === 'webrtc') return t('CONVERSATION.HEADER.WEBCALL.MODE_WEBRTC');
  return t('CONVERSATION.HEADER.WEBCALL.MODE_UNKNOWN');
});

const shouldShowModeLabel = computed(() => ['pbx', 'webrtc'].includes(webcallMode.value));

// UI-side safety: if backend/store lags, allow hiding the banner immediately after a successful reset.
const suppressBanner = ref(false);

const callStatus = computed(() => {
  if (webcallActiveFromConversation.value) return 'connected';
  if (webcallRequestStatus.value === 'pending') return 'ringing';
  return 'idle';
});
const isCallActive = computed(() => webcallActiveFromConversation.value);
const callDuration = ref('00:00');
let durationInterval = null;

const microphoneLevel = ref(0);
const speakerLevel = ref(0);

watch(
  () => webCallButtonRef.value?.microphoneLevel,
  newLevel => {
    microphoneLevel.value = Number(unref(newLevel)) || 0;
  },
  { immediate: true }
);

watch(
  () => webCallButtonRef.value?.speakerLevel,
  newLevel => {
    speakerLevel.value = Number(unref(newLevel)) || 0;
  },
  { immediate: true }
);

const isMicrophoneMuted = computed(() => toBool(unref(webCallButtonRef.value?.isMicrophoneMuted)));
const isSpeakerMuted = computed(() => toBool(unref(webCallButtonRef.value?.isSpeakerMuted)));

const canUseWebrtcControls = computed(() => webcallMode.value === 'webrtc');

const localVideoEl = ref(null);
const remoteVideoEl = ref(null);

const hasAnyVideoTrack = computed(() => {
  const local = unref(webCallButtonRef.value?.localStream);
  const remote = unref(webCallButtonRef.value?.remoteStream);
  const localHas = !!local?.getVideoTracks?.()?.length;
  const remoteHas = !!remote?.getVideoTracks?.()?.length;
  return localHas || remoteHas;
});

watch(
  () => webCallButtonRef.value?.localStream,
  stream => {
    if (localVideoEl.value) localVideoEl.value.srcObject = unref(stream) || null;
  },
  { immediate: true }
);

watch(
  () => webCallButtonRef.value?.remoteStream,
  stream => {
    if (remoteVideoEl.value) remoteVideoEl.value.srcObject = unref(stream) || null;
  },
  { immediate: true }
);

// IMPORTANT: getUserMedia must be triggered by user gesture on most browsers.
// So we DO NOT auto-start WebRTC on status=accepted. We show a "Join" action instead.
const canStartWebrtcSession = computed(() => {
  if (webcallRequestStatus.value !== 'accepted') return false;
  if (webcallMode.value !== 'webrtc') return false;
  if (!activeChatId.value) return false;
  // If already active, nothing to start (NOTE: exposed values are refs; must unref)
  if (unref(webCallButtonRef.value?.isCallActive)) return false;
  return true;
});

// Try to auto-start the agent WebRTC session when the customer accepts.
// Some browsers allow getUserMedia without an explicit user gesture; if it fails, the Join button remains as fallback.
const autoJoinAttemptedForCallId = ref('');

const canInvokeStartP2P = computed(() => {
  return typeof webCallButtonRef.value?.startP2PCall === 'function';
});

watch(
  () => [
    canStartWebrtcSession.value,
    conversationCustomAttributes.value?.webcall_request_call_id,
    canInvokeStartP2P.value,
  ],
  async ([canAutoJoin, requestCallId, canInvoke]) => {
    // Reset attempt marker when session/request isn't eligible anymore
    if (!canAutoJoin || !requestCallId) {
      autoJoinAttemptedForCallId.value = '';
      return;
    }
    if (!canInvoke) return; // WebCallButton ref not ready yet; retry when it becomes ready

    const key = String(requestCallId);
    if (autoJoinAttemptedForCallId.value === key) return;

    // Mark attempted only once we can actually invoke startP2PCall
    autoJoinAttemptedForCallId.value = key;
    await startAgentWebrtcSession();
  },
  { immediate: true }
);

const joinCallLabel = computed(() => {
  const translated = t('CONVERSATION.JOIN_CALL');
  // If translation is missing for current locale, vue-i18n often returns the key itself.
  if (translated === 'CONVERSATION.JOIN_CALL') return 'Join call';
  return translated;
});

const startAgentWebrtcSession = async () => {
  const requestCallId = conversationCustomAttributes.value?.webcall_request_call_id;
  if (!requestCallId) {
    useAlert(t('CONVERSATION.HEADER.WEBCALL.END_CALL_FAILED'), 'error');
    return;
  }
  suppressBanner.value = false;
  const ok = await webCallButtonRef.value?.startP2PCall?.({ callId: requestCallId });
  if (ok === false) {
    // startP2PCall already resets state + notifies widget; keep banner usable.
    useAlert(t('CONVERSATION.HEADER.WEBCALL.CALL_START_FAILED'), 'error');
  }
};


// If a new request comes in or a call becomes active, re-allow banner rendering.
watch(
  () => [activeChatId.value, webcallRequestStatus.value, webcallActiveFromConversation.value],
  ([, status, active]) => {
    if (active || status) suppressBanner.value = false;
  },
  { immediate: true }
);

const toggleMicrophoneMute = () => {
  if (!canUseWebrtcControls.value) return;
  webCallButtonRef.value?.toggleMicrophoneMute?.();
};

const toggleSpeakerMute = () => {
  if (!canUseWebrtcControls.value) return;
  webCallButtonRef.value?.toggleSpeakerMute?.();
};

const isCameraMuted = computed(() => !!unref(webCallButtonRef.value?.isCameraMuted));
const toggleCameraMute = () => {
  if (!canUseWebrtcControls.value) return;
  webCallButtonRef.value?.toggleCameraMute?.();
};

// On rejected/timeout, inform the agent and close the banner (unless a call actually started).
watch(
  () => webcallRequestStatus.value,
  status => {
    if (status === 'rejected') {
      useAlert('Müşteri aramayı reddetti');
      setTimeout(() => {
        if (webcallRequestStatus.value === 'rejected' && !webcallActiveFromConversation.value) {
          suppressBanner.value = true;
        }
      }, 1200);
    }
    if (status === 'timeout') {
      useAlert('Müşteri arama isteğine yanıt vermedi (zaman aşımı)');
      setTimeout(() => {
        if (webcallRequestStatus.value === 'timeout' && !webcallActiveFromConversation.value) {
          suppressBanner.value = true;
        }
      }, 1200);
    }
  }
);

// Watch conversation custom_attributes for webcall_active changes
watch(
  () => conversationCustomAttributes.value?.webcall_active,
  (newValue) => {
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }
    if (!toBool(newValue)) {
      callDuration.value = '00:00';
      return;
    }
    const startedAt = Number(conversationCustomAttributes.value?.webcall_started_at) || Math.floor(Date.now() / 1000);
    const update = () => {
      const elapsed = Math.max(0, Math.floor(Date.now() / 1000) - startedAt);
      const minutes = String(Math.floor(elapsed / 60)).padStart(2, '0');
      const seconds = String(elapsed % 60).padStart(2, '0');
      callDuration.value = `${minutes}:${seconds}`;
    };
    update();
    durationInterval = setInterval(update, 1000);
  },
  { immediate: true, deep: true }
);

onUnmounted(() => {
  if (durationInterval) clearInterval(durationInterval);
});

const callStatusText = computed(() => {
  if (webcallRequestBannerText.value) return webcallRequestBannerText.value;
  switch (callStatus.value) {
    case 'connecting':
      return t('CONVERSATION.HEADER.WEBCALL.CALL_CONNECTING');
    case 'ringing':
      return t('CONVERSATION.HEADER.WEBCALL.CALL_RINGING');
    case 'connected':
      return t('CONVERSATION.HEADER.WEBCALL.CALL_CONNECTED');
    case 'ended':
      return t('CONVERSATION.HEADER.WEBCALL.CALL_ENDED_SHORT');
    default:
      return '';
  }
});

const webcallDisabledByAgent = ref(false);

// Load webcallDisabledByAgent from conversation custom_attributes
const loadWebcallDisabledStatus = () => {
  const value = conversationCustomAttributes.value?.webcall_disabled_by_agent;
  webcallDisabledByAgent.value = toBool(value);
};

// Watch for conversation changes and load webcall disabled status
watch(
  () => activeChatId.value,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      loadWebcallDisabledStatus();
    }
  },
  { immediate: true }
);

// Also watch for custom_attributes changes
watch(
  () => conversationCustomAttributes.value?.webcall_disabled_by_agent,
  (newValue) => {
    if (newValue !== undefined) {
      webcallDisabledByAgent.value = toBool(newValue);
    }
  },
  { deep: true }
);

const requestWebcallFromCustomer = async (mode = 'pbx') => {
  if (!activeChatId.value) return;
  if (webcallRequestDisabledReason.value) {
    useAlert(webcallRequestDisabledReason.value, 'error');
    return;
  }
  // IMPORTANT: For WebRTC, grab permissions inside the user gesture (this click),
  // so the later auto-start after customer accepts isn't blocked by the browser.
  if (mode === 'webrtc') {
    const ok = await webCallButtonRef.value?.prewarmWebrtcMedia?.();
    if (ok === false) return;
  }
  try {
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/webcall/request`,
      { conversation_id: activeChatId.value, mode }
    );
    const data = response.data;
    if (data?.success) {
      useAlert(t('CONVERSATION.HEADER.WEBCALL.REQUEST_SENT'));
      suppressBanner.value = false;
      optimisticRequest.value = { status: 'pending', mode, at: Date.now() };
      await store.dispatch('conversations/getConversation', activeChatId.value);
    } else {
      useAlert(data?.error || t('CONVERSATION.HEADER.WEBCALL.REQUEST_FAILED'), 'error');
    }
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.WEBCALL.REQUEST_FAILED'), 'error');
  }
};

const canEndCall = computed(() => {
  // Allow reset/cancel whenever banner is shown (active call OR request state banner)
  return webcallActiveFromConversation.value || !!webcallRequestBannerText.value;
});

const toggleWebcall = async () => {
  if (!activeChatId.value) return;
  
  try {
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/webcall/toggle`,
      {
        conversation_id: activeChatId.value,
        disabled: !webcallDisabledByAgent.value,
      }
    );

    const data = response.data;
    if (data.success) {
      webcallDisabledByAgent.value = toBool(data.webcall_disabled_by_agent);
      // Refresh conversation from API so store reflects custom_attributes correctly
      await store.dispatch('conversations/getConversation', activeChatId.value);
      useAlert(data.message);
    }
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.WEBCALL.REQUEST_FAILED'), 'error');
  }
};

const endCall = async () => {
  const callId = conversationCustomAttributes.value?.webcall_call_id;
  if (!activeChatId.value) return;
  if (!callId) {
    // Stuck state: active flag true but call id missing. Reset state to recover UI.
    try {
      await axios.post(`/api/v1/accounts/${accountId.value}/webcall/reset`, {
        conversation_id: activeChatId.value,
      });
      useAlert(t('CONVERSATION.HEADER.WEBCALL.END_CALL_RESET'));
      suppressBanner.value = true;
      await store.dispatch('conversations/getConversation', activeChatId.value);
    } catch (error) {
      useAlert(t('CONVERSATION.HEADER.WEBCALL.END_CALL_FAILED'), 'error');
    }
    return;
  }

  try {
    await axios.post(`/api/v1/accounts/${accountId.value}/webcall/hangup`, {
      conversation_id: activeChatId.value,
      call_id: callId,
    });
    suppressBanner.value = true;
    await store.dispatch('conversations/getConversation', activeChatId.value);
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.WEBCALL.END_CALL_FAILED'), 'error');
  }
};

// NOTE: Direct /webcall/start requires offerSdp; dashboard flow uses /webcall/request and customer accepts in widget.
</script>

<template>
  <div class="relative">
    <!-- Mount WebRTC call handler for agent-side audio controls/status (kept hidden) -->
    <div class="hidden">
      <WebCallButton v-if="hasCurrentChatId" ref="webCallButtonRef" :conversation-id="activeChatId" />
    </div>
    <div
      ref="conversationHeader"
      class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
    >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <!-- Webcall request button (agent -> customer) -->
      <ButtonV4
        v-if="hasCurrentChatId"
        v-tooltip="webcallRequestDisabledReason || t('CONVERSATION.HEADER.WEBCALL.REQUEST_CALL')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-phone-call"
        class="rounded-md"
        :disabled="!!webcallRequestDisabledReason"
        @click="requestWebcallFromCustomer('pbx')"
      />
      <ButtonV4
        v-if="hasCurrentChatId"
        v-tooltip="webcallRequestDisabledReason || t('CONVERSATION.HEADER.WEBCALL.REQUEST_CALL')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-video"
        class="rounded-md"
        :disabled="!!webcallRequestDisabledReason"
        @click="requestWebcallFromCustomer('webrtc')"
      />
      <!-- WebCall Toggle Button - Sesli Aramayı Kilitle - Sürekli görünür -->
      <ButtonV4
        v-if="hasCurrentChatId"
        v-tooltip="
          webcallDisabledByAgent
            ? t('CONVERSATION.HEADER.WEBCALL.TOGGLE_ENABLE')
            : t('CONVERSATION.HEADER.WEBCALL.TOGGLE_DISABLE')
        "
        size="sm"
        variant="ghost"
        color="slate"
        :icon="webcallDisabledByAgent ? 'i-lucide-phone' : 'i-lucide-phone-off'"
        class="rounded-md"
        @click="toggleWebcall"
      />
      <MoreActions :conversation-id="activeChatId" />
      
    </div>
    </div>
    
    <!-- Call Status Banner - positioned absolutely below header -->
    <div
      v-if="
        !suppressBanner &&
        (webcallActiveFromConversation ||
          ((callStatus && callStatus !== 'idle' && callStatus !== 'ended') ||
            isCallActive ||
            !!webcallRequestBannerText))
      "
      class="top-full left-0 right-0 z-50 flex flex-col gap-2 px-4 py-2 bg-n-teal-10 text-white shadow-lg"
    >
      <!-- Split layout for readability: text is full-width, actions right-aligned -->
      <div class="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
        <div class="flex items-start gap-2 min-w-0">
          <fluent-icon icon="call" size="16" class="animate-pulse flex-shrink-0 text-white mt-0.5" />
          <div class="min-w-0">
            <div class="text-sm font-semibold text-white whitespace-normal break-words">
              {{ callStatusText }}
            </div>
            <div class="flex items-center gap-2 mt-1 flex-wrap">
              <span
                v-if="shouldShowModeLabel"
                class="text-xs px-2 py-0.5 rounded bg-white/20 text-white flex-shrink-0"
                v-tooltip="t('CONVERSATION.HEADER.WEBCALL.MODE_LABEL', { mode: webcallModeLabel })"
              >
                {{ webcallModeLabel }}
              </span>
              <span
                v-if="isCallActive && callStatus === 'connected'"
                class="text-xs font-mono text-white/90"
              >
                {{ t('CONVERSATION.HEADER.WEBCALL.CALL_DURATION', { duration: callDuration }) }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end gap-2 flex-wrap">
          <ButtonV4
            v-if="canStartWebrtcSession"
            size="sm"
            variant="solid"
            color="teal"
            icon="i-lucide-video"
            class="rounded-md"
            :label="joinCallLabel"
            @click="startAgentWebrtcSession"
          />
          <ButtonV4
            size="sm"
            variant="solid"
            color="ruby"
            icon="i-lucide-phone-off"
            class="rounded-md"
            v-tooltip="t('CONVERSATION.HEADER.WEBCALL.END_CALL')"
            :disabled="!canEndCall"
            @click="endCall"
          />
        </div>
      </div>

      <div v-if="isCallActive && callStatus === 'connected'" class="flex items-center gap-3">
        <!-- Mic -->
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <ButtonV4
            size="xs"
            variant="ghost"
            color="slate"
            :icon="isMicrophoneMuted ? 'i-lucide-mic-off' : 'i-lucide-mic'"
            class="rounded-md text-white hover:enabled:bg-white/10"
            :disabled="!canUseWebrtcControls"
            v-tooltip="
              canUseWebrtcControls
                ? (isMicrophoneMuted
                    ? t('CONVERSATION.HEADER.WEBCALL.UNMUTE_MIC')
                    : t('CONVERSATION.HEADER.WEBCALL.MUTE_MIC'))
                : t('CONVERSATION.HEADER.WEBCALL.PBX_CONTROLS_INFO')
            "
            @click="toggleMicrophoneMute"
          />
          <div class="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              class="h-full bg-white transition-all duration-100"
              :style="{ width: `${microphoneLevel}%` }"
            />
          </div>
        </div>

        <!-- Speaker -->
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <ButtonV4
            size="xs"
            variant="ghost"
            color="slate"
            :icon="isSpeakerMuted ? 'i-lucide-volume-x' : 'i-lucide-volume-2'"
            class="rounded-md text-white hover:enabled:bg-white/10"
            :disabled="!canUseWebrtcControls"
            v-tooltip="
              canUseWebrtcControls
                ? (isSpeakerMuted
                    ? t('CONVERSATION.HEADER.WEBCALL.UNMUTE_SPEAKER')
                    : t('CONVERSATION.HEADER.WEBCALL.MUTE_SPEAKER'))
                : t('CONVERSATION.HEADER.WEBCALL.PBX_CONTROLS_INFO')
            "
            @click="toggleSpeakerMute"
          />
          <div class="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              class="h-full bg-white transition-all duration-100"
              :style="{ width: `${speakerLevel}%` }"
            />
          </div>
        </div>

        <!-- Camera -->
        <div class="flex items-center justify-end">
          <ButtonV4
            size="xs"
            variant="ghost"
            color="slate"
            :icon="isCameraMuted ? 'i-lucide-video-off' : 'i-lucide-video'"
            class="rounded-md text-white hover:enabled:bg-white/10"
            :disabled="!canUseWebrtcControls"
            v-tooltip="isCameraMuted ? 'Kamerayı aç' : 'Kamerayı kapat'"
            @click="toggleCameraMute"
          />
        </div>
      </div>

      <div v-if="isCallActive && callStatus === 'connected' && !canUseWebrtcControls" class="text-xs text-white/90">
        {{ t('CONVERSATION.HEADER.WEBCALL.PBX_CONTROLS_INFO') }}
      </div>

      <!-- Video previews (WebRTC). Remote tile may stay black if bridge does not provide video. -->
      <div v-if="isCallActive && callStatus === 'connected' && canUseWebrtcControls && hasAnyVideoTrack" class="grid grid-cols-2 gap-2">
        <video
          ref="localVideoEl"
          class="w-full aspect-video bg-black/40 rounded-md object-cover"
          autoplay
          muted
          playsinline
        />
        <video
          ref="remoteVideoEl"
          class="w-full aspect-video bg-black/40 rounded-md object-cover"
          autoplay
          playsinline
        />
      </div>
    </div>
  </div>
</template>
